/**
 * Event Emitter for Inzif Widget SDK
 */

import type { WidgetEvents } from './types';
import { Logger } from './logger';

type EventCallback<T = any> = (data: T) => void;
type EventName = keyof WidgetEvents;

export class EventEmitter {
  private events: Map<EventName, Set<EventCallback>> = new Map();

  /**
   * Subscribe to an event
   */
  on<E extends EventName>(event: E, callback: EventCallback<WidgetEvents[E]>): void {
    if (!this.events.has(event)) {
      this.events.set(event, new Set());
    }
    this.events.get(event)!.add(callback);
  }

  /**
   * Subscribe to an event (alias for on)
   */
  addEventListener<E extends EventName>(event: E, callback: EventCallback<WidgetEvents[E]>): void {
    this.on(event, callback);
  }

  /**
   * Unsubscribe from an event
   */
  off<E extends EventName>(event: E, callback: EventCallback<WidgetEvents[E]>): void {
    const callbacks = this.events.get(event);
    if (callbacks) {
      callbacks.delete(callback);
      if (callbacks.size === 0) {
        this.events.delete(event);
      }
    }
  }

  /**
   * Unsubscribe from an event (alias for off)
   */
  removeEventListener<E extends EventName>(event: E, callback: EventCallback<WidgetEvents[E]>): void {
    this.off(event, callback);
  }

  /**
   * Subscribe to an event once
   */
  once<E extends EventName>(event: E, callback: EventCallback<WidgetEvents[E]>): void {
    const onceCallback: EventCallback<WidgetEvents[E]> = (data) => {
      callback(data);
      this.off(event, onceCallback);
    };
    this.on(event, onceCallback);
  }

  /**
   * Emit an event
   */
  emit<E extends EventName>(event: E, data: WidgetEvents[E]): void {
    const callbacks = this.events.get(event);
    if (callbacks) {
      callbacks.forEach(callback => {
        try {
          callback(data);
        } catch (error) {
          Logger.error(`Error in event handler for ${event}`, error);
        }
      });
    }
  }

  /**
   * Remove all event listeners
   */
  removeAllListeners(event?: EventName): void {
    if (event) {
      this.events.delete(event);
    } else {
      this.events.clear();
    }
  }

  /**
   * Get event listener count
   */
  listenerCount(event: EventName): number {
    const callbacks = this.events.get(event);
    return callbacks ? callbacks.size : 0;
  }

  /**
   * Get all event names that have listeners
   */
  eventNames(): EventName[] {
    return Array.from(this.events.keys());
  }
}